import { useRoleCoverageData } from './useRoleCoverageData';
import { useCoverageBoard } from './useCoverageBoard';
import { useRoleEditor } from './useRoleEditor';
import { useRoleKeyCreateForm } from './useRoleKeyCreateForm';

/**
 * EAA-1817: `/console/role-coverage` — "Liên kết vai trò", the role x
 * application coverage board (pairs with portal-api's EAA-1840).
 *
 * Composes four narrower hooks rather than one large one, per root CLAUDE.md's
 * split guidance:
 *   - `useRoleCoverageData`  — the four queries + capability flags every
 *     other piece reads from.
 *   - `useCoverageBoard`     — rail filtering/tabs + matrix cells + the
 *     "which app/role is selected" state.
 *   - `useRoleEditor`        — the selected app's editor card: rows, chip
 *     add/remove/restore, catalog sync, the undo window.
 *   - `useRoleKeyCreateForm` — the "Tạo vai trò chuẩn" empty-state CTA.
 *
 * EAA-1841: this is no longer its own route. `/console/role-coverage` and
 * `/console/role-map` were two renderings of ONE screen — design frames 42 and
 * 49 carry the same title ("Liên kết vai trò"), the same rows and the same
 * "Phạm vi" concept, and UX-SPEC §"/console/role-map — the only view" says so
 * outright. They are now the two view modes of `RoleMapPage`, and this hook
 * backs the **board** one. `active` is false while the list view is on screen.
 */
export function useRoleCoverage(active = true) {
    const data = useRoleCoverageData(active);
    const board = useCoverageBoard(data);
    const editor = useRoleEditor(data, board.selectedAppRow);
    const roleKeyCreate = useRoleKeyCreateForm(data.apiBase, data.token);

    const isLoading =
        data.coverageQuery.isLoading ||
        data.roleKeysQuery.isLoading ||
        data.adminAppsQuery.isLoading;
    const isError =
        data.coverageQuery.isError || data.roleKeysQuery.isError || data.adminAppsQuery.isError;

    function retry() {
        void data.coverageQuery.refetch();
        void data.roleKeysQuery.refetch();
        void data.adminAppsQuery.refetch();
        void data.roleMapsQuery.refetch();
    }

    return {
        canView: data.canView,
        canManage: data.canManage,
        canSync: data.canSync,
        isLoading,
        isError,
        retry,
        appCount: board.appRows.length,
        roleKeyCount: board.roleKeys.length,
        board,
        editor,
        roleKeyCreate,
    };
}

export type RoleCoverageVM = ReturnType<typeof useRoleCoverage>;
